import { createFileRoute } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import * as Sentry from '@sentry/react'
import { SITE_NAME, noindexMeta } from '@/lib/seo/siteMeta'

export const Route = createFileRoute('/_layout/test-error')({
  component: TestErrorPage,
  head: () => ({ meta: [{ title: `Test | ${SITE_NAME}` }, ...noindexMeta()] }),
})

function TestErrorPage() {
  const throwError = () => {
    throw new Error('Test error for Sentry - This is intentional!')
  }

  const captureException = () => {
    try {
      throw new Error('Manually captured test error')
    } catch (error) {
      Sentry.captureException(error)
      alert('Error captured and sent to Sentry!')
    }
  }

  const captureMessage = () => {
    Sentry.captureMessage('Test message from Sentry integration', 'info')
    alert('Message sent to Sentry!')
  }

  return (
    <div className="container mx-auto max-w-2xl px-4 py-12">
      <div className="space-y-6">
        <div>
          <h1 className="mb-2 text-3xl font-bold">Sentry Test Page</h1>
          <p className="text-slate-600">Test Sentry error tracking integration</p>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <h2 className="mb-4 text-xl font-semibold">Error Testing</h2>
          <div className="space-y-4">
            <div>
              <h3 className="mb-2 font-medium">1. Throw Uncaught Error</h3>
              <p className="mb-3 text-sm text-slate-600">
                This will throw an error that will be caught by the Error Boundary and sent to Sentry automatically.
              </p>
              <Button onClick={throwError} variant="destructive">
                Throw Error
              </Button>
            </div>

            <div className="border-t border-slate-200 pt-4">
              <h3 className="mb-2 font-medium">2. Capture Exception Manually</h3>
              <p className="mb-3 text-sm text-slate-600">
                This will manually capture an exception and send it to Sentry using Sentry.captureException().
              </p>
              <Button onClick={captureException} variant="outline">
                Capture Exception
              </Button>
            </div>

            <div className="border-t border-slate-200 pt-4">
              <h3 className="mb-2 font-medium">3. Send Message to Sentry</h3>
              <p className="mb-3 text-sm text-slate-600">
                This will send a test message to Sentry using Sentry.captureMessage().
              </p>
              <Button onClick={captureMessage} variant="outline">
                Send Message
              </Button>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <h3 className="mb-2 font-medium text-amber-900">Environment Configuration</h3>
          <p className="text-sm text-amber-800">
            Make sure you have set <code className="rounded bg-amber-100 px-1 py-0.5">VITE_SENTRY_DSN</code> in your{' '}
            <code className="rounded bg-amber-100 px-1 py-0.5">.env.local</code> file.
            <br />
            Sentry is only initialized in production mode or when the DSN is configured.
          </p>
        </div>
      </div>
    </div>
  )
}
